<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>

    <div class="p-relative header-image pb-5"></div>

    <b-container class="custom-container mt-5">
      <b-row>
        <b-col cols="12" class="px-3">
          <open-to-work-tabs />

          <h4 class="font-weight-bold pt-4 pb-1 align-middle">
            Searching for work
          </h4>
          <p class="text-muted mb-4">
            Let companies know you're available, and control what they can see
            about you.
          </p>

          <b-card
            class="otw-status-card mb-4"
            :class="
              form.enabled ? 'otw-status-card--on' : 'otw-status-card--off'
            "
            body-class="otw-status-card__body"
          >
            <div class="otw-status-card__icon">
              <b-icon :icon="form.enabled ? 'broadcast' : 'broadcast-pin'" />
            </div>
            <h5 class="otw-status-card__title font-weight-bold mb-1">
              {{
                form.enabled
                  ? "You're searching for work"
                  : 'Not searching for work'
              }}
            </h5>
            <p class="otw-status-card__desc text-muted small mb-0">
              When on, companies you don't currently work for can find you and
              request your resume.
            </p>
            <b-form-checkbox
              v-model="form.enabled"
              switch
              size="lg"
              class="otw-status-card__switch"
              :disabled="saving"
              @change="onToggleEnabled"
            />
          </b-card>

          <b-alert :show="!!enableError" variant="warning" class="mb-4">
            {{ enableError }}
          </b-alert>

          <b-form @submit.prevent="onSave">
            <b-card
              class="otw-section-card mb-4"
              body-class="otw-section-card__body"
            >
              <div class="otw-section-card__header">
                <span class="otw-section-card__icon">
                  <b-icon icon="briefcase-fill" />
                </span>
                <h6 class="otw-section-card__title">Employment Preference</h6>
              </div>
              <div class="otw-chip-group">
                <button
                  v-for="option in employmentTypeOptions"
                  :key="option"
                  type="button"
                  class="otw-chip"
                  :class="{
                    'otw-chip--active':
                      form.employment_preference.includes(option),
                  }"
                  @click="toggleArrayValue(form.employment_preference, option)"
                >
                  {{ option }}
                </button>
              </div>
            </b-card>

            <b-card
              class="otw-section-card mb-4"
              body-class="otw-section-card__body"
            >
              <div class="otw-section-card__header">
                <span class="otw-section-card__icon">
                  <b-icon icon="calendar-check-fill" />
                </span>
                <h6 class="otw-section-card__title">Availability</h6>
              </div>
              <div class="otw-chip-group">
                <button
                  v-for="opt in availabilityOptions"
                  :key="opt.value"
                  type="button"
                  class="otw-chip"
                  :class="{
                    'otw-chip--active': form.availability_type === opt.value,
                  }"
                  @click="form.availability_type = opt.value"
                >
                  {{ opt.text }}
                </button>
              </div>
              <b-form-datepicker
                v-if="form.availability_type === 'from_date'"
                v-model="form.availability_date"
                class="mt-3"
                placeholder="Available from"
              />
            </b-card>

            <b-card
              class="otw-section-card mb-4"
              body-class="otw-section-card__body"
            >
              <div class="otw-section-card__header">
                <span class="otw-section-card__icon">
                  <b-icon icon="geo-alt-fill" />
                </span>
                <h6 class="otw-section-card__title">
                  Preferred Work Locations
                </h6>
              </div>
              <div class="otw-chip-group">
                <button
                  v-for="option in locationOptions"
                  :key="option"
                  type="button"
                  class="otw-chip"
                  :class="{
                    'otw-chip--active':
                      form.preferred_locations.includes(option),
                  }"
                  @click="toggleArrayValue(form.preferred_locations, option)"
                >
                  {{ option }}
                </button>
              </div>
            </b-card>

            <b-card
              class="otw-section-card mb-4"
              body-class="otw-section-card__body"
            >
              <div class="otw-section-card__header">
                <span class="otw-section-card__icon">
                  <b-icon icon="award-fill" />
                </span>
                <h6 class="otw-section-card__title">Experience &amp; FIFO</h6>
              </div>
              <b-form-group
                label="Years of Experience"
                label-class="otw-field-label"
              >
                <v-select
                  v-model="form.years_experience"
                  :options="experienceOptions"
                  :reduce="(option) => option.value"
                  label="text"
                  :searchable="false"
                  :clearable="false"
                  class="otw-select"
                />
              </b-form-group>
              <b-form-checkbox v-model="form.fifo_open" class="mb-2">
                Open to FIFO
              </b-form-checkbox>
              <v-select
                v-if="form.fifo_open"
                v-model="form.roster_preference"
                :options="rosterOptions"
                :searchable="false"
                :clearable="false"
                class="otw-select"
              />
            </b-card>

            <b-card
              class="otw-section-card mb-4"
              body-class="otw-section-card__body"
            >
              <div class="otw-section-card__header">
                <span class="otw-section-card__icon">
                  <b-icon icon="chat-square-text-fill" />
                </span>
                <h6 class="otw-section-card__title">About Me</h6>
              </div>
              <b-form-textarea
                v-model="form.about_me"
                rows="4"
                placeholder="Describe your experience, skills and preferred work."
              />
            </b-card>

            <b-alert :show="!canEnable" variant="warning" class="mb-3">
              A resume isn't required - but if you don't have one on file, add
              your years of experience above before you can save your Open to
              Work profile.
              <router-link
                :to="{ name: 'user.settings-page', hash: '#profile' }"
              >
                Upload a resume
              </router-link>
              instead, if you'd rather.
            </b-alert>

            <div class="d-flex justify-content-end mb-5">
              <b-button
                type="submit"
                variant="primary"
                size="lg"
                :disabled="saving || !canEnable"
                class="px-5"
              >
                Save
              </b-button>
            </div>
          </b-form>
        </b-col>
      </b-row>
    </b-container>

    <b-alert
      class="custom-alert"
      :show="dismissCountDown"
      :variant="alertType"
      dismissible
      fade
      @dismiss-count-down="(val) => (dismissCountDown = val)"
    >
      {{ alertMsg }}
    </b-alert>
  </b-overlay>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import OpenToWorkTabs from '@/components/openToWork/OpenToWorkTabs.vue'

const EMPLOYMENT_TYPE_OPTIONS = [
  'Full Time',
  'Casual',
  'Contract',
  'FIFO',
  'Shutdown',
  'Labour Hire',
  'Apprenticeship',
]

// Matches DEFAULT_LOCATION_OPTIONS in views/candidates/ListPage.vue exactly
// (same 8 strings) — the BA-side Location filter does an exact match against
// whatever's in preferred_locations (whereJsonContains, not a substring
// search), so this list and that one have to agree on the literal values or
// a tradie's picked location can never match a BA's filter. This used to be
// WA-region names ('Perth Metro', 'Pilbara', etc.) while the filter already
// searched by full state — nothing a tradie picked here could ever match.
const LOCATION_OPTIONS = [
  'Australian Capital Territory',
  'New South Wales',
  'Northern Territory',
  'Queensland',
  'South Australia',
  'Tasmania',
  'Victoria',
  'Western Australia',
]

const EXPERIENCE_OPTIONS = [
  { value: '0-2', text: '0–2 Years' },
  { value: '3-5', text: '3–5 Years' },
  { value: '5-10', text: '5–10 Years' },
  { value: '10+', text: '10+ Years' },
]

const ROSTER_OPTIONS = ['2:1', '8:6', '7:7', 'flexible']

const AVAILABILITY_OPTIONS = [
  { value: 'immediate', text: 'Available Immediately' },
  { value: 'next_week', text: 'Available Next Week' },
  { value: 'from_date', text: 'Available From (Date)' },
]

function blankForm() {
  return {
    enabled: false,
    employment_preference: [],
    availability_type: 'immediate',
    availability_date: null,
    preferred_locations: [],
    years_experience: null,
    fifo_open: false,
    roster_preference: null,
    about_me: '',
  }
}

export default {
  name: 'OpenToWorkProfilePage',

  components: {
    OpenToWorkTabs,
    vSelect,
  },

  data() {
    return {
      isLoading: false,
      saving: false,
      enableError: '',
      form: blankForm(),
      dismissCountDown: 0,
      alertMsg: '',
      alertType: 'success',

      employmentTypeOptions: EMPLOYMENT_TYPE_OPTIONS,
      locationOptions: LOCATION_OPTIONS,
      experienceOptions: EXPERIENCE_OPTIONS,
      rosterOptions: ROSTER_OPTIONS,
      availabilityOptions: AVAILABILITY_OPTIONS,
    }
  },

  computed: {
    ...mapGetters('tradieOtw', ['profile']),
    ...mapGetters('auth', ['user']),

    hasResume() {
      return !!(this.user && this.user.resume)
    },

    // Mirrors the server-side gate in TradieOtwService::update() (422 "Add a
    // resume, or select your years of experience...") so the tradie sees it
    // up front instead of after a failed submit. A resume is no longer
    // mandatory — years_experience is the fallback requirement when there
    // isn't one.
    canEnable() {
      return this.hasResume || !!this.form.years_experience
    },
  },

  async created() {
    await this.loadProfile()
  },

  methods: {
    ...mapActions('tradieOtw', ['fetchProfile', 'updateProfile']),

    async loadProfile() {
      this.isLoading = true
      try {
        const data = await this.fetchProfile()
        this.form = {
          ...blankForm(),
          ...data,
        }
      } catch (err) {
        this.onError(err)
      } finally {
        this.isLoading = false
      }
    },

    toggleArrayValue(list, value) {
      const index = list.indexOf(value)
      if (index === -1) list.push(value)
      else list.splice(index, 1)
    },

    // The toggle saves immediately (matches the mobile-first "flip it and
    // it's live" mental model for on/off switches) rather than waiting for
    // the full form's Save button.
    async onToggleEnabled(value) {
      this.enableError = ''
      this.saving = true
      try {
        await this.updateProfile({ enabled: value })
        this.showToast(
          'Success',
          value
            ? 'Searching for work turned on.'
            : 'Searching for work turned off.',
        )
      } catch (err) {
        this.form.enabled = !value
        if (err && err.status === 422 && err.data && err.data.message) {
          this.enableError = err.data.message
        } else {
          this.onError(err)
        }
      } finally {
        this.saving = false
      }
    },

    async onSave() {
      this.saving = true
      this.enableError = ''
      try {
        await this.updateProfile(this.form)
        this.showToast('Success', 'Profile updated.')
      } catch (err) {
        if (err && err.status === 422 && err.data && err.data.message) {
          this.enableError = err.data.message
        } else {
          this.onError(err)
        }
      } finally {
        this.saving = false
      }
    },

    onError(err) {
      const message =
        (err && err.data && err.data.message) ||
        (err && err.message) ||
        'Something went wrong.'
      this.alertMsg = message
      this.alertType = 'danger'
      this.dismissCountDown = 5
    },

    showToast(title, message) {
      this.alertMsg = message
      this.alertType = 'success'
      this.dismissCountDown = 3
    },
  },
}
</script>

<style lang="scss" scoped>
.otw-status-card {
  border-radius: 12px;
}

// Grid instead of flex-wrap on purpose: a wrapping flex row eventually runs
// out of horizontal space and drops the switch onto its own line (with nasty
// leftover whitespace wherever it lands). A grid's columns don't wrap — the
// icon and switch columns stay fixed-width and pinned left/right at every
// card width. So the switch never relocates, on any screen size.
//
// Only icon/title/switch share the top row — the description gets its own
// full-width row below (grid-column spans all 3 columns via the area name
// repeating), rather than being squeezed into the same narrow middle column
// as the title on both rows. Squeezing it there was the "feels thin" bug:
// with the icon and switch columns both eating into it, the description had
// only a sliver of the card's width to wrap in and turned into a tall,
// narrow ladder of text. Giving it the full row uses almost the whole card
// width, so it wraps like normal body text instead.
/deep/ .otw-status-card__body {
  display: grid;
  grid-template-columns: 48px 1fr auto;
  grid-template-areas:
    'icon title switch'
    'desc desc desc';
  align-items: center;
  column-gap: 1rem;
  row-gap: 0.4rem;
  padding: 1.5rem;
  border-radius: 12px;
  transition: background-color 0.2s ease;
}

.otw-status-card--on /deep/ .otw-status-card__body {
  background-color: #eaf6ee;
}

.otw-status-card--off /deep/ .otw-status-card__body {
  background-color: #f5f7f7;
}

.otw-status-card__title {
  grid-area: title;
}

.otw-status-card__desc {
  grid-area: desc;
}

// The switch sits in the top row next to the title and is vertically
// centered against it — the .custom-control wrapper itself is already
// grid-centered via align-items:center on .otw-status-card__body, but the
// switch you actually *see* is drawn by ::before (track) / ::after (thumb),
// positioned absolutely with fixed rem offsets meant for a normal text
// label. With no label text here, those fixed offsets land ~19px below true
// center instead of centering on the (correctly-centered) label box.
// `top: 50%` + `margin-top: -half-their-own-height` re-centers each pseudo
// on the label's own midpoint — robust to the row growing/shrinking (e.g.
// the title wrapping to a 2nd line), unlike a hardcoded pixel shift.
// `::after`'s existing `transform: translateX(...)` slide animation is left
// alone since this only touches `top`/`margin-top`, not `transform`.
.otw-status-card__switch {
  grid-area: switch;
  align-self: center;
  margin-bottom: 0;

  /deep/ .custom-control-label {
    &::before {
      top: 50%;
      margin-top: -16px;
    }

    &::after {
      top: 50%;
      margin-top: -14px;
    }
  }
}

.otw-status-card__icon {
  grid-area: icon;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  font-size: 1.4rem;
  flex-shrink: 0;
}

.otw-status-card--on .otw-status-card__icon {
  background-color: #d1f0db;
  color: #1f7a3a;
}

.otw-status-card--off .otw-status-card__icon {
  background-color: #e9ecef;
  color: #6c7581;
}

.otw-section-card {
  border-radius: 12px;
}

/deep/ .otw-section-card__body {
  padding: 1.5rem;
}

.otw-section-card__header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.1rem;
}

.otw-section-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: #e6f0ff;
  color: #006cff;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.otw-section-card__title {
  font-weight: 700;
  font-size: 0.95rem;
  margin-bottom: 0;
}

/deep/ .otw-field-label {
  font-weight: 600;
  font-size: 0.85rem;
  color: #78849e;
}

.otw-chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.otw-chip {
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  border: 1px solid #d7dee7;
  background-color: #fff;
  color: #495057;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.2;
  transition: all 0.15s ease;
}

.otw-chip:hover {
  border-color: #006cff;
  color: #006cff;
}

.otw-chip--active {
  background-color: #006cff;
  border-color: #006cff;
  color: #fff;
}

.otw-chip--active:hover {
  color: #fff;
}

// Was b-form-select (a plain native <select>) — its open option list is
// browser-drawn, not page CSS, so its width can't be constrained to the
// trigger's width at all. At narrow viewports (320px) the popup rendered
// wider than the card and bled off the right edge of the screen. vue-select
// (already used the same way in CategoryFilter.vue) renders its own DOM
// dropdown instead, so it's a normal block element that respects the
// trigger's width like everything else on the page. Themed via its CSS
// custom properties to match the app's global .custom-select/.form-control
// look (see _forms.scss) rather than fighting its internal classes.
.otw-select {
  --vs-border-color: #e5e5e5;
  --vs-border-width: 2px;
  --vs-border-radius: 8px;
  --vs-search-input-color: #495057;
}

.otw-select.vs--open {
  --vs-border-color: #2c66d5;
}

::v-deep .otw-select .vs__dropdown-toggle {
  min-height: 50px;
  height: auto;
  align-items: center;
}

::v-deep .otw-select .vs__selected {
  color: #495057;
}
</style>
